import React, { useEffect, useMemo, useState } from "react";
import PrimaryButton from "../../../shared/ui/buttons/PrimaryButton";

function SoftCountBadge({ children }) {
  return (
    <span className="rounded-full bg-slate-700/60 text-slate-300 px-2.5 py-0.5 text-xs">
      {children}
    </span>
  );
}

function SectionCard({ title, right, children }) {
  return (
    <section className="rounded-2xl border border-slate-700/70 bg-gradient-to-b from-slate-800/70 to-slate-900/40 backdrop-blur-md shadow-sm">
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-700/60">
        <div className="flex items-center gap-3">
          <span className="inline-block h-5 w-1 rounded bg-emerald-500" />
          <h3 className="text-slate-100 text-[40px] font-semibold">{title}</h3>
        </div>
        {right}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function collectPayload(schema) {
  if (!schema) return undefined;
  const fields = Array.isArray(schema.fields)
    ? schema.fields
    : schema
    ? [schema]
    : [];
  const payload = {};
  for (const f of fields) {
    const label =
      (f.label || f.name || "field") +
      (f.required ? " *" : "") +
      (f.help ? `\n(${f.help})` : "");
    const ph =
      f.placeholder ||
      (f.type === "number"
        ? "e.g., 1"
        : f.type === "select"
        ? (f.options || []).join(" | ")
        : "");
    const raw = window.prompt(`${label}`, ph);
    if (raw == null) return null;
    if (f.type === "number") {
      const n = Number(raw);
      if (Number.isNaN(n)) return null;
      payload[f.name] = n;
    } else if (f.type === "select") {
      if (f.options && !f.options.includes(raw)) {
        alert("گزینهٔ نامعتبر.");
        return null;
      }
      payload[f.name] = raw;
    } else if (f.type === "array") {
      payload[f.name] = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (f.minLength && payload[f.name].length < f.minLength) return null;
      if (f.maxLength && payload[f.name].length > f.maxLength) return null;
    } else {
      payload[f.name] = raw;
    }
  }
  return payload;
}

const LS_KEY_VIS = "aio:operator:visibility:v1";
const LS_KEY_ORDER = "aio:operator:order:v1";

export default function AllInOneActions({
  operatorConfig,
  selectedDay,
  updateDay,
  dispatchAction,
  toActionKey,
}) {
  const [open, setOpen] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const dayActionGroups = useMemo(() => {
    return (operatorConfig?.days || []).map((d) => {
      const key = toActionKey ? toActionKey(d.id) : d.id;
      const actions = operatorConfig?.actions?.[key] || [];
      return { dayId: d.id, dayLabel: d.label || d.id, actions };
    });
  }, [operatorConfig, toActionKey]);

  const allCount = useMemo(
    () => dayActionGroups.reduce((sum, g) => sum + (g.actions?.length || 0), 0),
    [dayActionGroups]
  );

  const buildDefaultVisibility = () => {
    const days = {};
    const actions = {};
    for (const g of dayActionGroups) {
      days[g.dayId] = true;
      actions[g.dayId] = {};
      for (const a of g.actions) actions[g.dayId][a.id] = true;
    }
    return { days, actions };
  };

  const buildDefaultOrder = () => {
    const order = {};
    for (const g of dayActionGroups) {
      order[g.dayId] = (g.actions || []).map((a) => a.id);
    }
    return order;
  };

  const [visibility, setVisibility] = useState(buildDefaultVisibility);
  const [draft, setDraft] = useState(visibility);
  const [order, setOrder] = useState(buildDefaultOrder);
  const [orderDraft, setOrderDraft] = useState(order);
  const [search, setSearch] = useState("");
  const [showAllForOrdering, setShowAllForOrdering] = useState(false);

  // load persisted vis + order
  useEffect(() => {
    try {
      const rawVis = localStorage.getItem(LS_KEY_VIS);
      if (rawVis) {
        const parsed = JSON.parse(rawVis);
        setVisibility(parsed);
        setDraft(parsed);
      }
    } catch {}

    try {
      const rawOrder = localStorage.getItem(LS_KEY_ORDER);
      if (rawOrder) {
        const parsed = JSON.parse(rawOrder);
        setOrder(parsed);
        setOrderDraft(parsed);
      }
    } catch {}
  }, []);

  // Rebuild vis + merge with persisted when config changes
  useEffect(() => {
    setVisibility(() => {
      const next = buildDefaultVisibility();
      try {
        const raw = localStorage.getItem(LS_KEY_VIS);
        if (raw) {
          const persisted = JSON.parse(raw);
          for (const d of Object.keys(next.days)) {
            if (persisted.days?.[d] !== undefined)
              next.days[d] = persisted.days[d];
            for (const a of Object.keys(next.actions[d])) {
              if (persisted.actions?.[d]?.[a] !== undefined)
                next.actions[d][a] = persisted.actions[d][a];
            }
          }
        }
      } catch {}
      setDraft(next);
      return next;
    });

    // Build/merge order: keep old order for known ids, append any new ids at the end
    setOrder(() => {
      const base = buildDefaultOrder();
      try {
        const raw = localStorage.getItem(LS_KEY_ORDER);
        if (raw) {
          const persisted = JSON.parse(raw) || {};
          const merged = {};
          for (const g of dayActionGroups) {
            const idsNow = base[g.dayId] || [];
            const prev = Array.isArray(persisted[g.dayId])
              ? persisted[g.dayId]
              : [];
            const kept = prev.filter((id) => idsNow.includes(id));
            const extras = idsNow.filter((id) => !kept.includes(id));
            merged[g.dayId] = [...kept, ...extras];
          }
          setOrderDraft(merged);
          return merged;
        }
      } catch {}
      setOrderDraft(base);
      return base;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorConfig, toActionKey]);

  const persistVisibility = (v) => {
    try {
      localStorage.setItem(LS_KEY_VIS, JSON.stringify(v));
    } catch {}
  };
  const persistOrder = (o) => {
    try {
      localStorage.setItem(LS_KEY_ORDER, JSON.stringify(o));
    } catch {}
  };

  // sort by saved order per day
  const sortActionsForDay = (dayId, actions) => {
    const list = order[dayId] || [];
    const idx = new Map(list.map((id, i) => [id, i]));
    return [...actions].sort((a, b) => {
      const ia = idx.has(a.id) ? idx.get(a.id) : Number.MAX_SAFE_INTEGER;
      const ib = idx.has(b.id) ? idx.get(b.id) : Number.MAX_SAFE_INTEGER;
      if (ia !== ib) return ia - ib;
      return (a.label || a.id).localeCompare(b.label || b.id);
    });
  };

  // filtered groups (respect visibility + search) then sorted by order
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dayActionGroups
      .map((g) => {
        const acts = g.actions.filter((a) => {
          const enabled = visibility.actions[g.dayId]?.[a.id];
          if (!enabled) return false;
          if (!q) return true;
          return (
            a.label?.toLowerCase().includes(q) ||
            a.id?.toLowerCase().includes(q)
          );
        });
        return { ...g, actions: sortActionsForDay(g.dayId, acts) };
      })
      .filter((g) => g.actions.length > 0);
  }, [dayActionGroups, visibility, search, order]);

  const runDayAction = (dayId, action) => {
    let payload;
    if (action?.payloadSchema) {
      const p = collectPayload(action.payloadSchema);
      if (p === null) return;
      payload = p;
    }
    if (dayId && dayId !== selectedDay) {
      updateDay(dayId);
    }
    setTimeout(() => {
      dispatchAction(action.id, payload);
    }, 0);
  };

  const countVisible = filteredGroups.reduce((s, g) => s + g.actions.length, 0);

  // visibility toggles
  const setDayChecked = (dayId, checked) => {
    setDraft((d) => {
      const next = { ...d, days: { ...d.days }, actions: { ...d.actions } };
      next.days[dayId] = checked;
      next.actions[dayId] = { ...(next.actions[dayId] || {}) };
      const group = dayActionGroups.find((gg) => gg.dayId === dayId);
      if (group) {
        for (const a of group.actions) next.actions[dayId][a.id] = checked;
      }
      return next;
    });
  };

  const setActionChecked = (dayId, actionId, checked) => {
    setDraft((d) => {
      const next = { ...d, actions: { ...d.actions }, days: { ...d.days } };
      next.actions[dayId] = { ...(next.actions[dayId] || {}) };
      next.actions[dayId][actionId] = checked;
      const group = dayActionGroups.find((g) => g.dayId === dayId);
      if (group) {
        const allChecked = group.actions.every(
          (a) => next.actions[dayId][a.id]
        );
        const someChecked = group.actions.some(
          (a) => next.actions[dayId][a.id]
        );
        next.days[dayId] = allChecked ? true : someChecked ? false : false;
      }
      return next;
    });
  };

  const selectAll = () => setDraft(buildDefaultVisibility());

  const selectNone = () => {
    const none = buildDefaultVisibility();
    for (const d of Object.keys(none.days)) none.days[d] = false;
    for (const d of Object.keys(none.actions))
      for (const a of Object.keys(none.actions[d])) none.actions[d][a] = false;
    setDraft(none);
  };

  // ---- ORDERING LOGIC ----
  const moveAction = (dayId, actionId, dir) => {
    setOrderDraft((prev) => {
      const arr = [...(prev[dayId] || [])];
      const i = arr.indexOf(actionId);
      if (i === -1) return prev;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...prev, [dayId]: arr };
    });
  };

  // NEW: set position by entering a number + Enter
  const setOrderPosition = (dayId, actionId, posInput) => {
    setOrderDraft((prev) => {
      const arr = [...(prev[dayId] || [])];
      const current = arr.indexOf(actionId);
      if (current === -1) return prev;

      let pos = Number(posInput);
      if (!Number.isFinite(pos)) return prev;

      // convert 1-based to 0-based and clamp
      pos = Math.floor(pos) - 1;
      pos = Math.max(0, Math.min(arr.length - 1, pos));

      // move item
      arr.splice(current, 1);
      arr.splice(pos, 0, actionId);
      return { ...prev, [dayId]: arr };
    });
  };

  const resetOrderForDay = (dayId) => {
    const defaultArr =
      (dayActionGroups.find((g) => g.dayId === dayId)?.actions || []).map(
        (a) => a.id
      );
    setOrderDraft((prev) => ({ ...prev, [dayId]: defaultArr }));
  };

  // Apply config (visibility + order)
  const applyConfig = () => {
    setVisibility(draft);
    persistVisibility(draft);

    // Clean order for non-existing ids and keep structure
    const cleaned = {};
    for (const g of dayActionGroups) {
      const validIds = (g.actions || []).map((a) => a.id);
      const current = Array.isArray(orderDraft[g.dayId])
        ? orderDraft[g.dayId]
        : [];
      const keep = current.filter((id) => validIds.includes(id));
      const extras = validIds.filter((id) => !keep.includes(id));
      cleaned[g.dayId] = [...keep, ...extras];
    }
    setOrder(cleaned);
    persistOrder(cleaned);

    setSearch("");
    setShowConfig(false);
  };

  const cancelConfig = () => {
    setDraft(visibility);
    setOrderDraft(order);
    setShowConfig(false);
  };

  // Helpers for ordering view
  const actionsForOrderingView = (g) => {
    const ids = orderDraft[g.dayId] || [];
    const map = new Map((g.actions || []).map((a) => [a.id, a]));
    let list = ids.map((id) => map.get(id)).filter(Boolean);

    if (!showAllForOrdering) {
      const checkedSet = new Set(
        Object.entries(draft.actions[g.dayId] || {})
          .filter(([, v]) => v)
          .map(([k]) => k)
      );
      list = list.filter((a) => checkedSet.has(a.id));
    }
    return list;
  };

  return (
    <>
      <SectionCard
        title="اکشن‌های همه‌روزه (All-in-one)"
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowConfig(true)}
              className="text-[40px] rounded-lg px-3 py-1.5 bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
            >
              پیکربندی
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              className="text-[40px] rounded-lg px-3 py-1.5 bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
              aria-expanded={open}
            >
              {open ? "بستن" : "باز کردن"}
            </button>
          </div>
        }
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-slate-400 text-sm">
            نمایش بر اساس پیکربندی شما. روی هر مورد کلیک کنید تا ابتدا روز مرتبط
            فعال و سپس اکشن اجرا شود.
          </p>
          <div className="flex items-center gap-2">
            <SoftCountBadge>{countVisible}</SoftCountBadge>
          </div>
        </div>

        <div
          className={[
            "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
            open ? "opacity-100 " : "opacity-0 max-h-0",
          ].join(" ")}
        >
          <div className="mb-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجوی اکشن…"
              className="w-full md:w-80 rounded-xl border border-slate-700/70 bg-slate-900/60 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>

          {filteredGroups.length === 0 ? (
            <div className="text-slate-500 text-sm">موردی برای نمایش نیست.</div>
          ) : (
            <div className="space-y-6">
              {filteredGroups.map((group) => (
                <div
                  key={group.dayId}
                  className="rounded-xl border border-slate-700/60 p-4 bg-slate-900/40"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-slate-200 font-semibold">
                      {group.dayLabel}
                    </h4>
                    <SoftCountBadge>{group.actions.length}</SoftCountBadge>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 text-[50px]">
                    {group.actions.map((a) => (
                      <PrimaryButton
                        key={`${group.dayId}:${a.id}`}
                        text={a.label}
                        onClick={() => runDayAction(group.dayId, a)}
                        className="w-full rounded-xl bg-indigo-600 text-white hover:bg-indigo-600/90 focus-visible:ring-2 focus-visible:ring-indigo-500/60 transition"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {showConfig && (
        <div style={{ scale: "1.2" }} className="fixed inset-0 z-[9999] ">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={cancelConfig}
          />
          <div className="absolute left-1/2 top-1/2 w-[90vw] max-w-5xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-700/70 bg-slate-900/95 shadow-xl">
            <div className="flex flex-col gap-3 px-5 py-4 border-b border-slate-700/60">
              <div className="flex items-center justify-between">
                <h3 className="text-slate-100 text-lg font-semibold">
                  پیکربندی نمایش و ترتیب
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAll}
                    className="text-xs rounded-lg px-3 py-1.5 bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
                  >
                    انتخاب همه
                  </button>
                  <button
                    onClick={selectNone}
                    className="text-xs rounded-lg px-3 py-1.5 bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
                  >
                    عدم انتخاب همه
                  </button>
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-slate-200 text-sm">
                <input
                  type="checkbox"
                  checked={showAllForOrdering}
                  onChange={(e) => setShowAllForOrdering(e.target.checked)}
                />
                نمایش همهٔ اکشن‌ها برای ترتیب (در غیر این صورت فقط انتخاب‌شده‌ها)
              </label>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5 space-y-5">
              {dayActionGroups.map((g) => {
                const allActions = g.actions || [];
                const dayChecked = !!draft.days[g.dayId];
                const someChecked =
                  allActions.length > 0 &&
                  allActions.some((a) => draft.actions[g.dayId]?.[a.id]);
                const allChecked =
                  allActions.length > 0 &&
                  allActions.every((a) => draft.actions[g.dayId]?.[a.id]);

                const listForOrder = actionsForOrderingView(g);

                return (
                  <div
                    key={g.dayId}
                    className="rounded-xl border border-slate-700/60 p-4 bg-slate-900/50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={dayChecked && allChecked}
                          ref={(el) => {
                            if (el)
                              el.indeterminate = !allChecked && someChecked;
                          }}
                          onChange={(e) =>
                            setDayChecked(g.dayId, e.target.checked)
                          }
                        />
                        <span className="text-slate-200 font-medium">
                          {g.dayLabel}
                        </span>
                      </label>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => resetOrderForDay(g.dayId)}
                          className="text-xs rounded-lg px-3 py-1.5 bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
                        >
                          ریست ترتیب
                        </button>
                        <SoftCountBadge>{allActions.length}</SoftCountBadge>
                      </div>
                    </div>

                    {allActions.length === 0 ? (
                      <div className="text-slate-500 text-sm">
                        اکشنی ثبت نشده است.
                      </div>
                    ) : (
                      <>
                        {/* visibility checkboxes */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 mb-4">
                          {allActions.map((a) => (
                            <label
                              key={a.id}
                              className="flex items-center gap-2 rounded-xl border border-slate-700/60 bg-slate-900/40 px-3 py-2"
                            >
                              <input
                                type="checkbox"
                                checked={!!draft.actions[g.dayId]?.[a.id]}
                                onChange={(e) =>
                                  setActionChecked(
                                    g.dayId,
                                    a.id,
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-slate-200 text-sm">
                                {a.label}
                              </span>
                            </label>
                          ))}
                        </div>

                        {/* ordering list (with number field) */}
                        <div className="rounded-xl border border-slate-700/60">
                          <div className="px-3 py-2 border-b border-slate-700/60 text-slate-300 text-sm">
                            ترتیب نمایش دکمه‌ها
                          </div>
                          {listForOrder.length === 0 ? (
                            <div className="px-3 py-3 text-slate-500 text-sm">
                              موردی برای مرتب‌سازی نیست.
                            </div>
                          ) : (
                            <ul className="divide-y divide-slate-800">
                              {listForOrder.map((a) => {
                                const arr = orderDraft[g.dayId] || [];
                                const i = arr.indexOf(a.id);
                                const canUp = i > 0;
                                const canDown = i >= 0 && i < arr.length - 1;
                                return (
                                  <li
                                    key={`ord:${g.dayId}:${a.id}`}
                                    className="flex items-center justify-between px-3 py-2 gap-3"
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      {/* position input */}
                                      <input
                                        type="number"
                                        min={1}
                                        max={arr.length}
                                        defaultValue={i + 1}
                                        title={`عدد ۱ تا ${arr.length} و Enter`}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            const val = (e.target).value;
                                            setOrderPosition(
                                              g.dayId,
                                              a.id,
                                              val
                                            );
                                          }
                                        }}
                                        className="w-16 rounded-md border border-slate-700 bg-slate-900/60 text-slate-100 text-sm px-2 py-1 text-center"
                                      />
                                      <span className="truncate text-slate-100 text-sm">
                                        {a.label}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <button
                                        disabled={!canUp}
                                        onClick={() =>
                                          moveAction(g.dayId, a.id, "up")
                                        }
                                        className={`rounded-md px-2 py-1 text-xs ${
                                          canUp
                                            ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                                            : "bg-slate-900/60 text-slate-600 cursor-not-allowed"
                                        }`}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        disabled={!canDown}
                                        onClick={() =>
                                          moveAction(g.dayId, a.id, "down")
                                        }
                                        className={`rounded-md px-2 py-1 text-xs ${
                                          canDown
                                            ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                                            : "bg-slate-900/60 text-slate-600 cursor-not-allowed"
                                        }`}
                                      >
                                        ↓
                                      </button>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-700/60">
              <button
                onClick={cancelConfig}
                className="rounded-lg px-3 py-2 text-sm bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
              >
                انصراف
              </button>
              <button
                onClick={applyConfig}
                className="rounded-lg px-3 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-600/90 transition"
              >
                ذخیره
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
